import { useRef } from 'react';

export interface EscapeClosesPopupOnly {
  /** Snapshot whether the popup was open before react-aria handles the key (and closes it). */
  onKeyDownCapture: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Keep that Escape from reaching an enclosing drawer/dialog. */
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Escape with the popup open closes the popup and nothing else; with it closed, Escape is left alone
 * so an enclosing SideDrawer / Dialog still closes.
 *
 * react-aria's combobox deliberately lets Escape keep propagating when `allowsCustomValue` is set
 * (ours always is), so without this the same keydown also reached MUI's modal handler and a
 * half-filled form was lost (TAS-3660 QA, Add Product commodity picker).
 *
 * The open state is read from `aria-expanded` in the capture phase, before react-aria closes the
 * popup in its own handler — by the time a bubble handler runs the state may already say "closed".
 * Stopping propagation in the bubble phase (not capture) leaves react-aria's own handler, which
 * reverts the input text, untouched.
 */
export function useEscapeClosesPopupOnly(): EscapeClosesPopupOnly {
  const popupWasOpenRef = useRef(false);

  return {
    onKeyDownCapture: (e) => {
      popupWasOpenRef.current = e.key === 'Escape' && e.currentTarget.getAttribute('aria-expanded') === 'true';
    },
    onKeyDown: (e) => {
      if (e.key !== 'Escape' || !popupWasOpenRef.current) return;
      popupWasOpenRef.current = false;
      e.stopPropagation();
    },
  };
}
